import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import type { RosterEntry } from '@4cm/core/rosterEntry';
import type { LoadState } from '../loadState';

interface UseRosterEntriesOptions {
  /** Bump to force a re-fetch (e.g. after coach adds / edits an entry). */
  refreshKey?: number;
  limit?: number;
}

interface RosterEntriesResult {
  rows: RosterEntry[];
  state: LoadState;
  errorMessage?: string;
}

/**
 * Live list of `roster_entries` rows for a team — the under-13 ghost
 * players (see migration 00084 + docs/spec/22a-v1-launch-posture.md).
 *
 * Filters out soft-deleted rows on the client (deleted_at IS NULL
 * could go through the query but the explicit client filter keeps
 * the rule visible). Sorted by jersey number nulls last, then last
 * name + first name — same order coaches expect on paper rosters.
 */
export function useRosterEntries(
  teamId: string | undefined,
  teamLoading: boolean,
  opts: UseRosterEntriesOptions = {},
): RosterEntriesResult {
  const limit = opts.limit ?? 60;
  const refreshKey = opts.refreshKey ?? 0;
  const [result, setResult] = useState<RosterEntriesResult>({
    rows: [],
    state: 'loading',
  });

  useEffect(() => {
    if (teamLoading) {
      setResult({ rows: [], state: 'loading' });
      return;
    }
    if (!teamId) {
      setResult({ rows: [], state: 'empty' });
      return;
    }
    let cancelled = false;
    setResult({ rows: [], state: 'loading' });

    (async () => {
      try {
        const { data, error } = await supabase
          .from('roster_entries')
          .select(
            'id, team_id, first_name, last_name, dob, position, jersey_number, parent_user_id, claimed_user_id, notes, created_by, created_at, updated_at, deleted_at',
          )
          .eq('team_id', teamId)
          .is('deleted_at', null)
          .limit(limit);
        if (cancelled) return;
        if (error) {
          setResult({ rows: [], state: 'error', errorMessage: error.message });
          return;
        }
        const rows = (data ?? []) as RosterEntry[];
        rows.sort((a, b) => {
          const aJ = a.jersey_number ?? Infinity;
          const bJ = b.jersey_number ?? Infinity;
          if (aJ !== bJ) return aJ - bJ;
          const lastCmp = a.last_name.localeCompare(b.last_name);
          if (lastCmp !== 0) return lastCmp;
          return a.first_name.localeCompare(b.first_name);
        });
        setResult({
          rows,
          state: rows.length === 0 ? 'empty' : 'ok',
        });
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'fetch failed';
        setResult({ rows: [], state: 'error', errorMessage: msg });
      }
    })();

    return () => { cancelled = true; };
  }, [teamId, teamLoading, limit, refreshKey]);

  return result;
}
